import { useAppSSE } from 'contexts/AppSSEContext';
import {
  deriveCheckpointsFromTour,
  ensureLatLng,
  toCheckpoint,
} from 'features/Responder/features/Tours/utils/normalizeTourNodes';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import apiClient from 'services/_infra/apiClient';

const resolveNodeRadiusMeters = (node = {}, fallback = 75) => {
  const radius = Number(
    node?.radiusMeters ?? node?.radius ?? node?.geometry?.radiusMeters ?? node?.geometry?.radius,
  );
  if (Number.isFinite(radius) && radius > 0) return radius;
  return fallback;
};

const resolveNodeHandleBearing = (node = {}, fallback = 0) => {
  const bearing = Number(
    node?.handleBearing ??
      node?.geometry?.handleBearing ??
      node?.geometry?.angleRad ??
      node?.geometry?.bearingRad,
  );
  return Number.isFinite(bearing) ? bearing : fallback;
};

/**
 * @typedef {{ coordinates: [number, number], address?: string, lat?: number|string, lng?: number|string, placeId?: string }} TourStop
 */
/**
 * @typedef {{ id?: string|number, name?: string, stops: TourStop[], checkpoints?: TourCheckpoint[], polyline?: any }} Tour
 */
/**
 * @typedef {{ id?: string|number, lat?: number, lng?: number, radiusMeters?: number, isCheckpoint?: boolean, timeWindowSeconds?: number, etaSeconds?: number, stopIndex?: number, handleBearing?: number }} TourCheckpoint
 */
/**
 * @typedef {Object} TourContextValue
 * @property {Tour[]} tours
 * @property {Tour|null} activeTourData
 * @property {[number, number]|null} center
 * @property {{ address: string, lat: string|number, lng: string|number, placeId: string, useCurrent: boolean, zoneId?: string|number|null, geometry?: any }} startLocation
 * @property {'DRIVING'|'WALKING'|'INDOOR'} travelMode
 * @property {boolean} snapToRoads
 * @property {Array<{ id: string|number, label: string, address: string, lat: string|number, lng: string|number, placeId: string }>} stops
 * @property {any} routeData
 * @property {TourCheckpoint[]} checkpoints
 * @property {string|number|null} selectedCheckpointId
 * @property {string|null} mapTool
 * @property {(tours: Tour[]) => void} setTours
 * @property {(tour: Tour|null) => void} setActiveTourData
 * @property {([number, number]|null) => void} setCenter
 * @property {(loc: any) => void} setStartLocation
 * @property {(stops: any[]) => void} setStops
 * @property {(route: any) => void} setRouteData
 * @property {(cps: TourCheckpoint[]) => void} setCheckpoints
 * @property {(id: string|number|null) => void} setSelectedCheckpointId
 * @property {(tool: string|null) => void} setMapTool
 * @property {(checkpoint: Partial<TourCheckpoint>) => string|number} addCheckpoint
 * @property {(id: string|number, updates: Partial<TourCheckpoint>) => void} updateCheckpoint
 * @property {(id: string|number) => void} removeCheckpoint
 * @property {(tour: Tour) => void} addTour
 * @property {(tour: Tour|null) => void} selectTour
 * @property {() => void} clearActiveTour
 * @property {Tour|null} builderTourData
 * @property {(tour: Tour|null) => void} setBuilderTourData
 * @property {{ current: string|number|null }} builderHydrationRef
 * @property {(mode: 'DRIVING'|'WALKING'|'INDOOR') => void} setTravelMode
 * @property {(snap: boolean) => void} setSnapToRoads
 * @property {boolean} tourEditing
 * @property {(editing: boolean) => void} setTourEditing
 */

const TourContext = createContext();

/**
 * TourProvider exposes responder tour state and helpers.
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const TourProvider = ({ children, disableTourFetch = false }) => {
  const { subscribe } = useAppSSE();
  const [center, setCenter] = useState(null);
  const [tours, setTours] = useState([]);
  const [activeTourData, setActiveTourData] = useState(null);
  const emptyStart = {
    address: '',
    lat: '',
    lng: '',
    placeId: '',
    useCurrent: false,
    zoneId: null,
    geometry: null,
    radiusMeters: 25,
    handleBearing: 0,
    loopDestinationRadiusMeters: 100,
    loopDestinationHandleBearing: 0,
  };
  const [startLocation, setStartLocation] = useState(emptyStart);
  const [travelMode, setTravelMode] = useState('DRIVING');
  const [snapToRoads, setSnapToRoads] = useState(true);
  const [builderTourData, setBuilderTourData] = useState(null);
  const [tourEditing, setTourEditing] = useState(false);
  const builderHydrationRef = useRef(null);
  const [builderResetSignal, setBuilderResetSignal] = useState(0);

  // const [stops, setStops] = useState([
  // 	{ id: 1, label: 'Stop 1', address: '', lat: '', lng: '', placeId: '' },
  // ]);
  const [routeData, setRouteData] = useState(null);
  // Checkpoints are the single source of truth for waypoints (formerly stops).
  const [checkpoints, setCheckpoints] = useState([]);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState(null);
  const [mapTool, setMapTool] = useState(null);
  // Alias to keep legacy stop consumers working while using checkpoints only.
  const stops = checkpoints;
  const setStops = setCheckpoints;

  // Update center when active tour changes
  useEffect(() => {
    const almostEqual = (a, b, tol = 1e-6) => Math.abs(a - b) < tol;
    const coordsEqual = (a, b) =>
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length >= 2 &&
      b.length >= 2 &&
      almostEqual(Number(a[0]), Number(b[0])) &&
      almostEqual(Number(a[1]), Number(b[1]));

    const nextCenter =
      activeTourData?.stops?.[0]?.coordinates || activeTourData?.coordinates?.[0] || null;

    if (!nextCenter) return;

    if (coordsEqual(nextCenter, center)) return;

    setCenter(nextCenter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourData]);

  // Default snapping behavior when travel mode changes
  useEffect(() => {
    // Indoor mode handling disabled for now; keep snap-to-roads as-is.
    if (!snapToRoads) setSnapToRoads(true);
  }, [travelMode, snapToRoads]);

  // Snap map center to explicitly chosen start location
  useEffect(() => {
    const rawLat = startLocation?.lat;
    const rawLng = startLocation?.lng;
    if (rawLat === '' || rawLng === '' || rawLat == null || rawLng == null) return;
    const lat = Number(rawLat);
    const lng = Number(rawLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setCenter([lat, lng]);
  }, [startLocation?.lat, startLocation?.lng]);

  useEffect(() => {
    if (disableTourFetch) return undefined;

    const unsub = subscribe('tour-update', (data) => {
      try {
        updateToursInMemory(data);
      } catch (err) {
        console.error('Failed to handle tour-update payload', err);
      }
    });

    fetchTours();

    return unsub;
  }, [disableTourFetch, subscribe]);

  const updateToursInMemory = (tourUpdates) => {
    // return;
    setTours((prevTours) => {
      const updated = [...prevTours];
      Object.values(tourUpdates).forEach((tour) => {
        const index = updated.findIndex((t) => `${t.id}` === `${tour.id}`);
        if (index !== -1) {
          updated[index] = tour;
        } else {
          updated.push(tour);
        }
      });
      return updated;
    });
  };

  const fetchTours = async () => {
    try {
      const res = await apiClient.get(`/responder/tours`);
      if (!res || res.status !== 200 || !res.data) {
        return;
      }
      if (Array.isArray(res.data.tours)) {
        setTours(res.data.tours);
      } else {
        console.warn('[TourContext] Unexpected tour payload', res.data);
      }
    } catch (err) {
      console.error('[TourContext] fetchTours error', err);
    }
  };

  const resolveTourByIdPayload = (data, targetId) => {
    const key = `${targetId}`;
    const direct =
      data?.tour ||
      data?.trip ||
      data?.result ||
      data?.data ||
      (Array.isArray(data?.tours) ? data.tours.find((item) => `${item?.id}` === key) : null) ||
      (data && typeof data === 'object' ? data : null);
    return direct && `${direct?.id}` === key ? direct : null;
  };

  const fetchTourById = async (id) => {
    if (id == null) return null;
    const key = `${id}`;
    const endpoint = `/responder/tours/${encodeURIComponent(key)}`;
    try {
      const res = await apiClient.get(endpoint);
      const resolved = resolveTourByIdPayload(res?.data, key);
      if (!resolved) return null;
      addTour(resolved);
      return resolved;
    } catch (err) {
      console.error('[TourContext] fetchTourById error', err);
      return null;
    }
  };

  /**
   * Append a new tour to the in-memory list.
   * @param {Tour} tour
   */
  const addTour = (tour) => {
    if (!tour) return;
    setTours((prevTours) => {
      const exists = prevTours.some((t) => `${t.id}` === `${tour.id}`);
      if (exists) return prevTours.map((t) => (`${t.id}` === `${tour.id}` ? tour : t));
      return [...prevTours, tour];
    });
  };

  const updateTour = (tour) => {
    if (!tour?.id) return;
    setTours((prevTours) => prevTours.map((t) => (`${t.id}` === `${tour.id}` ? tour : t)));
  };

  const deleteTour = async (id) => {
    if (id == null) return null;
    const deletingActiveTour = !!(activeTourData && `${activeTourData.id}` === `${id}`);
    try {
      const res = await apiClient.delete(`/responder/tours/${id}`);
      setTours((prevTours) => prevTours.filter((t) => `${t.id}` !== `${id}`));
      setActiveTourData((prev) => {
        if (prev && `${prev.id}` === `${id}`) return null;
        return prev;
      });
      setBuilderTourData((prev) => {
        if (prev && `${prev.id}` === `${id}`) return null;
        return prev;
      });
      if (deletingActiveTour) {
        setRouteData(null);
        setCheckpoints([]);
        setSelectedCheckpointId(null);
        setStartLocation(emptyStart);
      }
      return res;
    } catch (error) {
      console.error('[TourContext] deleteTour error', error);
      throw error;
    }
  };

  const hydrateTourWithStops = (tour) => {
    if (!tour) return null;
    const { originNode, checkpoints: checkpointList } = deriveCheckpointsFromTour(tour);
    const originCp = originNode ? toCheckpoint(originNode, -1) : null;
    const allCheckpoints = originCp ? [originCp, ...checkpointList] : checkpointList;
    return {
      ...tour,
      originNode,
      stops: allCheckpoints,
      checkpoints: allCheckpoints,
    };
  };

  /**
   * Set the active tour (used by TourMap/TourList).
   * @param {Tour|null} tour
   */
  const selectTour = (tour) => {
    if (!tour) {
      setActiveTourData(null);
      setRouteData(null);
      setCheckpoints([]);
      setSelectedCheckpointId(null);
      setStartLocation(emptyStart);
      return;
    }
    const hydrated = hydrateTourWithStops(tour);
    const originNode = hydrated?.originNode || tour?.originNode;
    const originCoords = Array.isArray(originNode?.coords)
      ? originNode.coords
      : Array.isArray(originNode?.coordinates)
        ? originNode.coordinates
        : null;
    const { lat: resolvedLat, lng: resolvedLng } = ensureLatLng(originNode);
    const originLat = Number(
      originCoords?.[0] ?? resolvedLat ?? originNode?.lat ?? originNode?.latitude,
    );
    const originLng = Number(
      originCoords?.[1] ?? resolvedLng ?? originNode?.lng ?? originNode?.longitude,
    );
    const originRadiusMeters = resolveNodeRadiusMeters(originNode, 25);
    const originHandleBearing = resolveNodeHandleBearing(originNode, 0);
    const loopDestinationNode = Array.isArray(tour?.nodes)
      ? tour.nodes
          .slice()
          .reverse()
          .find((node) => {
            const kind = `${node?.type ?? node?.kind ?? ''}`.toLowerCase().trim();
            return kind === 'destination' || kind === 'end';
          })
      : null;
    const loopDestinationRadiusMeters = resolveNodeRadiusMeters(loopDestinationNode, 100);
    const loopDestinationHandleBearing = resolveNodeHandleBearing(loopDestinationNode, 0);

    setActiveTourData(hydrated);
    setCheckpoints(hydrated?.checkpoints || []);
    if (Number.isFinite(originLat) && Number.isFinite(originLng)) {
      const originZoneId = originNode?.zoneId ?? originNode?.geometry?.zoneId ?? null;
      const originGeometry = originNode?.geometry ?? null;
      setStartLocation({
        address: originNode?.address || '',
        lat: originLat,
        lng: originLng,
        placeId: originNode?.placeId || '',
        useCurrent: false,
        zoneId: originZoneId,
        geometry: originGeometry,
        radiusMeters: originRadiusMeters,
        handleBearing: originHandleBearing,
        loopDestinationRadiusMeters,
        loopDestinationHandleBearing,
      });
    } else {
      setStartLocation(emptyStart);
    }
    const hasPolyline = tour?.encodedPolyline || tour?.polyline;
    const legsFromNodes = Array.isArray(tour?.nodes)
      ? tour.nodes
          .map((node, idx) =>
            idx === 0
              ? null
              : {
                  travelMode: node.travelMode || node.mode || 'DRIVING',
                  duration: node.duration ?? null,
                  distanceMeters: node.distanceMeters ?? null,
                  polyline: node.encodedPolyline || node.polyline || null,
                },
          )
          .filter(Boolean)
      : [];
    const legs = Array.isArray(tour?.legs) && tour.legs.length ? tour.legs : legsFromNodes;
    const parseDurationSeconds = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const n = parseFloat(val.replace('s', ''));
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    };
    const totalDistanceMeters =
      tour?.totalDistanceMeters ??
      legs.reduce(
        (sum, leg) => sum + (Number.isFinite(leg?.distanceMeters) ? Number(leg.distanceMeters) : 0),
        0,
      );
    const totalDuration =
      tour?.totalDuration ??
      legs.reduce((sum, leg) => sum + parseDurationSeconds(leg?.duration), 0);

    if (hasPolyline || legs.length) {
      setRouteData({
        polyline: tour?.encodedPolyline || tour?.polyline || null,
        legs,
        totalDuration,
        totalDistanceMeters,
      });
    } else {
      setRouteData(null);
    }
  };

  const setTourForBuilder = (tour) => {
    if (!tour) {
      setBuilderTourData(null);
      setCheckpoints([]);
      return;
    }
    const hydrated = hydrateTourWithStops(tour);
    setBuilderTourData({
      ...hydrated,
    });
    setCheckpoints(hydrated?.checkpoints || []);
  };

  /**
   * Add a checkpoint with defaults and return its generated id.
   * @param {Partial<TourCheckpoint>} checkpoint
   * @returns {string|number}
   */
  const addCheckpoint = (checkpoint) => {
    const id = crypto.randomUUID?.() || Date.now();
    const nextIndex = checkpoints.length;
    const geometryType =
      checkpoint?.geometry?.type || checkpoint?.geometryType || checkpoint?.shapeType || 'circle';
    const label =
      typeof checkpoint?.label === 'string'
        ? checkpoint.label
        : `Checkpoint ${String.fromCharCode(65 + nextIndex)}`;
    setCheckpoints((prev) => [
      ...prev,
      {
        id,
        label,
        stopIndex: nextIndex + 1,
        radiusMeters: 75,
        isCheckpoint: true,
        timeWindowSeconds: null,
        timeWindowMinutes: 0,
        stopTimeMinutes: 0,
        floor: '',
        isBadgeRequired: false,
        hasElevator: false,
        hasStairs: true,
        indoorNotes: '',
        kind: 'checkpoint',
        mode: checkpoint?.mode || 'DRIVING',
        geometry: {
          type: geometryType,
          radiusMeters: checkpoint?.radiusMeters || 75,
          ...(checkpoint?.geometry || {}),
        },
        ...checkpoint,
      },
    ]);
    return id;
  };
  /**
   * Clear the active tour selection.
   */
  const clearActiveTour = () => {
    setActiveTourData(null);
    setCheckpoints([]);
    setSelectedCheckpointId(null);
  };

  /**
   * Shallow-merge metadata updates (e.g. name, isLooped) into builderTourData
   * without triggering checkpoint re-hydration.
   * @param {Partial<Tour>} updates
   */
  const updateBuilderTourMeta = (updates) => {
    // When builderTourData is null (new tour), create a minimal object so that
    // fields like `name` are available to useRoutePersistence without triggering
    // checkpoint re-hydration (this bypasses setTourForBuilder intentionally).
    setBuilderTourData((prev) => (prev != null ? { ...prev, ...updates } : { ...updates }));
  };

  const resetBuilderState = () => {
    setActiveTourData(null);
    setBuilderTourData(null);
    setRouteData(null);
    setCheckpoints([]);
    setSelectedCheckpointId(null);
    setStartLocation(emptyStart);
    setCenter(null);
    setMapTool(null);
  };

  /**
   * Shallow-merge updates into an existing checkpoint.
   * @param {string|number} id
   * @param {Partial<TourCheckpoint>} updates
   */
  const updateCheckpoint = (id, updates) => {
    setCheckpoints((prev) => prev.map((cp) => (cp.id === id ? { ...cp, ...updates } : cp)));
  };

  /**
   * Remove a checkpoint and clear selection if it was active.
   * @param {string|number} id
   */
  const removeCheckpoint = (id) => {
    setCheckpoints((prev) => prev.filter((cp) => cp.id !== id));
    if (selectedCheckpointId === id) setSelectedCheckpointId(null);
  };

  const contextValue = {
    // State
    tours,
    activeTourData,
    center,
    startLocation,
    stops,
    routeData,
    checkpoints,
    selectedCheckpointId,
    mapTool,
    // Setters
    setTours,
    setActiveTourData,
    setCenter,
    setStartLocation,
    setStops,
    setRouteData,
    setCheckpoints,
    setSelectedCheckpointId,
    setMapTool,
    addCheckpoint,
    updateCheckpoint,
    removeCheckpoint,
    setTravelMode,
    setSnapToRoads,
    travelMode,
    snapToRoads,

    // Helper functions
    addTour,
    updateTour,
    fetchTourById,
    deleteTour,
    selectTour,
    clearActiveTour,
    resetBuilderState,
    updateBuilderTourMeta,

    builderTourData,
    setBuilderTourData: setTourForBuilder,
    tourEditing,
    setTourEditing,
    builderHydrationRef,
    builderResetSignal,
    requestBuilderReset: () => setBuilderResetSignal((prev) => prev + 1),
  };

  return <TourContext.Provider value={contextValue}>{children}</TourContext.Provider>;
};

/**
 * Hook to access responder tour state and helpers.
 * @returns {TourContextValue}
 */
export const useTours = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTours must be used within a TourProvider');
  }
  return context;
};

export { TourContext };
